import { useState, useEffect, useCallback } from "react";
import {
  Bot, RefreshCw, Plus, Copy, KeyRound, ShieldAlert, ShieldCheck, Trash2,
  Power, PowerOff, CheckCircle2, XCircle, AlertTriangle, Loader2, Server,
  Database, Wrench, ListChecks, PlugZap, ClipboardList, Clock, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../utils/api";

const BASE = `${API_URL}/api/mcp-oauth`;
const TEAL = "#255E69";

const TABS = [
  { id: "status", label: "Serverstatus", icon: Server },
  { id: "clients", label: "OAuth-klienter", icon: KeyRound },
  { id: "sessions", label: "Sessioner", icon: PlugZap },
  { id: "tools", label: "Verktyg", icon: Wrench },
  { id: "sources", label: "Datakällor", icon: Database },
  { id: "install", label: "Installation", icon: ListChecks },
  { id: "audit", label: "Audit", icon: ClipboardList },
];

function copy(text, label = "Kopierat") {
  navigator.clipboard.writeText(text).then(() => toast.success(label));
}

function StatusPill({ status }) {
  const map = {
    ok: { c: "bg-green-100 text-green-700", Icon: CheckCircle2, t: "OK" },
    connected: { c: "bg-green-100 text-green-700", Icon: CheckCircle2, t: "Ansluten" },
    not_connected: { c: "bg-amber-100 text-amber-700", Icon: AlertTriangle, t: "Ej ansluten" },
    error: { c: "bg-red-100 text-red-700", Icon: XCircle, t: "Fel" },
  };
  const m = map[status] || map.error;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.c}`}>
      <m.Icon className="w-3.5 h-3.5" /> {m.t}
    </span>
  );
}

function Field({ label, value, mono = true, canCopy = true }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-sm text-gray-900 truncate ${mono ? "font-mono" : ""}`} title={value}>
          {value ?? "—"}
        </span>
        {canCopy && value && (
          <button onClick={() => copy(value)} className="text-gray-400 hover:text-gray-700 shrink-0"
            data-testid="copy-field-btn" title="Kopiera">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ChatGptConnection() {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState("status");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [denied, setDenied] = useState([]);
  const [audit, setAudit] = useState([]);
  const [newSecret, setNewSecret] = useState(null); // {client_id, client_secret, name}
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", redirect_uris: "", enable_write: false });

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BASE}${path}`, {
        ...opts,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders(), ...(opts.headers || {}) },
      }),
    [getAuthHeaders]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, se, d, a] = await Promise.all([
        authFetch("/admin/status"), authFetch("/admin/clients"), authFetch("/admin/sessions"),
        authFetch("/admin/denied-redirects"), authFetch("/admin/audit?limit=100"),
      ]);
      if (s.ok) setStatus((await s.json()));
      if (c.ok) setClients((await c.json()).clients || []);
      if (se.ok) setSessions((await se.json()).sessions || []);
      if (d.ok) setDenied((await d.json()).denied_redirects || []);
      if (a.ok) setAudit((await a.json()).events || []);
    } catch (e) {
      toast.error("Kunde inte hämta MCP-status");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createClient = async () => {
    if (!form.name.trim()) return toast.error("Ange ett namn");
    setCreating(true);
    try {
      const uris = form.redirect_uris.split("\n").map((u) => u.trim()).filter(Boolean);
      const r = await authFetch("/admin/clients", {
        method: "POST", body: JSON.stringify({ name: form.name.trim(), redirect_uris: uris, enable_write: form.enable_write }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setNewSecret(data);
      setForm({ name: "", redirect_uris: "", enable_write: false });
      toast.success("OAuth-klient skapad");
      loadAll();
    } catch { toast.error("Kunde inte skapa klient"); }
    finally { setCreating(false); }
  };

  const rotateSecret = async (cid) => {
    if (!window.confirm("Rotera client_secret? Den gamla slutar fungera direkt.")) return;
    const r = await authFetch(`/admin/clients/${cid}/rotate-secret`, { method: "POST" });
    if (r.ok) { const d = await r.json(); setNewSecret({ ...d, name: cid }); toast.success("Ny secret genererad"); loadAll(); }
    else toast.error("Kunde inte rotera");
  };

  const toggleActive = async (cid, active) => {
    const r = await authFetch(`/admin/clients/${cid}/active`, { method: "PUT", body: JSON.stringify({ active: !active }) });
    if (r.ok) { toast.success(!active ? "Klient aktiverad" : "Klient inaktiverad + sessioner återkallade"); loadAll(); }
    else toast.error("Misslyckades");
  };

  const setClientWrite = async (cid, enable) => {
    if (enable) {
      const ok = window.confirm(
        "Aktivera SKRIV-behörighet (elido.write) för denna klient?\n\n" +
        "Klienten kan då via ChatGPT ändra Elidos SEO-sidor med apply_seo_changes och " +
        "rollback_seo_change (titel/meta, status, noindex, slug, omdirigeringar, sammanslagning, pensionering). " +
        "Ge bara detta till en anslutning du helt litar på. Varje ändring kräver fortfarande explicit consent i ChatGPT.\n\n" +
        "OBS: Efter aktivering måste du koppla OM ChatGPT-anslutningen — en redan pågående session får INTE det nya scopet automatiskt."
      );
      if (!ok) return;
    }
    const r = await authFetch(`/admin/clients/${cid}/write`, { method: "PUT", body: JSON.stringify({ enable }) });
    if (r.ok) {
      toast.success(enable
        ? "Write aktiverat — koppla om ChatGPT-anslutningen för att få det nya scopet"
        : "Write inaktiverat + klientens sessioner återkallade");
      loadAll();
    } else toast.error("Kunde inte ändra write-behörighet");
  };

  const setRedirectUris = async (cid, uris) => {
    const r = await authFetch(`/admin/clients/${cid}/redirect-uris`, { method: "PUT", body: JSON.stringify({ redirect_uris: uris }) });
    if (r.ok) { toast.success("Redirect-URI:er uppdaterade"); loadAll(); }
    else toast.error("Misslyckades");
  };

  const approveDenied = async (client_id, redirect_uri) => {
    const client = clients.find((c) => c.client_id === client_id);
    if (!client) return;
    const next = Array.from(new Set([...(client.redirect_uris || []), redirect_uri]));
    await setRedirectUris(client_id, next);
  };

  const revokeSession = async (sid) => {
    const r = await authFetch(`/admin/sessions/${sid}/revoke`, { method: "POST" });
    if (r.ok) { toast.success("Session återkallad"); loadAll(); } else toast.error("Misslyckades");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="chatgpt-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const srv = status?.server || {};

  return (
    <div className="space-y-6" data-testid="chatgpt-connection-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${TEAL}15` }}>
            <Bot className="w-5 h-5" style={{ color: TEAL }} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Elido Intelligence — ChatGPT</h1>
            <p className="text-sm text-gray-500">MCP-anslutning för ChatGPT (OAuth 2.1, PKCE). 14 read-only verktyg + 2 skrivskyddade SEO-actions (elido.write).</p>
          </div>
        </div>
        <button onClick={loadAll} className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" /> Uppdatera
        </button>
      </div>

      {/* newly created secret (shown once) */}
      {newSecret && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="new-secret-box">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">Client secret visas endast en gång</p>
              <p className="text-sm text-amber-800 mb-3">Kopiera och klistra in i ChatGPT nu. Den kan inte visas igen.</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-xs font-mono truncate">{newSecret.client_id}</code>
                  <button onClick={() => copy(newSecret.client_id, "client_id kopierat")} className="px-2 py-2 bg-white border border-amber-200 rounded" data-testid="copy-new-client-id"><Copy className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-xs font-mono truncate">{newSecret.client_secret}</code>
                  <button onClick={() => copy(newSecret.client_secret, "client_secret kopierat")} className="px-2 py-2 bg-white border border-amber-200 rounded" data-testid="copy-new-client-secret"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <button onClick={() => setNewSecret(null)} className="mt-3 text-sm text-amber-900 font-medium hover:underline" data-testid="dismiss-secret">Jag har sparat den</button>
            </div>
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            style={tab === t.id ? { borderColor: TEAL, color: TEAL } : {}}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* STATUS */}
      {tab === "status" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="tab-content-status">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Server</h3>
            <Field label="MCP-endpoint" value={srv.mcp_endpoint} />
            <Field label="Issuer" value={srv.issuer} />
            <Field label="Resource" value={srv.resource} />
            <Field label="Miljö" value={srv.environment} mono={false} />
            <Field label="Transport" value={srv.transport} mono={false} canCopy={false} />
            <Field label="Auth" value={srv.auth} mono={false} canCopy={false} />
            <Field label="Read scope" value={srv.scope} />
            <Field label="Write scope" value={srv.write_scope || "elido.write"} />
            <Field label="Scopes som stöds" value={(srv.scopes_supported || []).join(" ") || "—"} mono={false} canCopy={false} />
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Hälsa</h3>
            <Field label="Senaste lyckade anrop" value={srv.last_successful_call || "—"} mono={false} canCopy={false} />
            <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Senaste fel</span>
              <span className="text-sm">{srv.last_error ? <StatusPill status="error" /> : <StatusPill status="ok" />}</span>
            </div>
            <Field label="Verktyg (read-only / write / totalt)"
              value={srv.tool_counts ? `${srv.tool_counts.read_only} / ${srv.tool_counts.write} / ${srv.tool_counts.total}` : "—"}
              mono={false} canCopy={false} />
            <Field label="Antal klienter" value={String(srv.client_count ?? 0)} mono={false} canCopy={false} />
            <Field label="Write-aktiverade klienter" value={String(srv.write_enabled_clients ?? 0)} mono={false} canCopy={false} />
            <Field label="Aktiva sessioner" value={String(srv.active_session_count ?? 0)} mono={false} canCopy={false} />
            <Field label="Protected resource metadata" value={srv.protected_resource_metadata} />
            <Field label="Authorization server metadata" value={srv.authorization_server_metadata} />
          </div>
        </div>
      )}

      {/* CLIENTS */}
      {tab === "clients" && (
        <div className="space-y-4" data-testid="tab-content-clients">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Skapa förregistrerad klient</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Namn</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ChatGPT (Preview)" data-testid="client-name-input"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Redirect-URI:er (en per rad, exakt matchning)</label>
                <textarea value={form.redirect_uris} onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })}
                  rows={2} placeholder="Lämna tom om du inte vet ChatGPTs callback ännu" data-testid="client-redirects-input"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-gray-200" />
              </div>
            </div>
            <label className="mt-3 flex items-start gap-2 cursor-pointer select-none" data-testid="client-enable-write">
              <input type="checkbox" checked={form.enable_write}
                onChange={(e) => setForm({ ...form, enable_write: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-gray-700">
                Ge <code className="font-mono text-amber-800">elido.write</code> (skriv-behörighet) direkt
                <span className="block text-xs text-amber-700">Endast för betrodda anslutningar. Klienten kan då ändra SEO-sidor via ChatGPT. Kan även slås på/av senare per klient. Standard är read-only.</span>
              </span>
            </label>
            <button onClick={createClient} disabled={creating} data-testid="create-client-btn"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50" style={{ background: TEAL }}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Skapa klient
            </button>
          </div>

          {denied.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="denied-redirects">
              <h4 className="font-semibold text-amber-900 flex items-center gap-2 mb-2"><ShieldAlert className="w-4 h-4" /> Nekade redirect-URI:er (kandidater)</h4>
              <p className="text-sm text-amber-800 mb-3">ChatGPT försökte ansluta med en redirect_uri som inte är registrerad. Godkänn manuellt om den är korrekt.</p>
              <div className="space-y-2">
                {denied.map((d, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <code className="text-xs font-mono text-gray-900 block truncate">{d.redirect_uri}</code>
                      <span className="text-xs text-gray-500">klient {d.client_id} · {d.count}× · senast {d.last_seen}</span>
                    </div>
                    <button onClick={() => approveDenied(d.client_id, d.redirect_uri)} data-testid="approve-denied-btn"
                      className="shrink-0 px-3 py-1.5 text-xs font-medium text-white rounded-lg" style={{ background: TEAL }}>Godkänn</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {clients.length === 0 && <p className="text-sm text-gray-500">Inga klienter ännu.</p>}
            {clients.map((c) => (
              <ClientCard key={c.client_id} c={c} onRotate={rotateSecret} onToggle={toggleActive}
                onSaveRedirects={setRedirectUris} onCopy={copy} onSetWrite={setClientWrite} />
            ))}
          </div>
        </div>
      )}

      {/* SESSIONS */}
      {tab === "sessions" && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="tab-content-sessions">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Admin</th><th className="text-left px-4 py-3">Klient</th><th className="text-left px-4 py-3">Scope</th><th className="text-left px-4 py-3">Family</th><th className="text-left px-4 py-3">Access utgår</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Inga sessioner</td></tr>}
              {sessions.map((s) => (
                <tr key={s.session_id} className="border-t border-gray-100">
                  <td className="px-4 py-3">{s.admin_email}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.client_id}</td>
                  <td className="px-4 py-3 text-xs">{s.scope}</td>
                  <td className="px-4 py-3"><StatusPill status={s.refresh_family_status === "active" ? "ok" : "error"} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{s.access_expires_at}</td>
                  <td className="px-4 py-3 text-right">
                    {s.refresh_family_status === "active" && (
                      <button onClick={() => revokeSession(s.session_id)} data-testid="revoke-session-btn"
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Återkalla</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOOLS */}
      {tab === "tools" && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="tab-content-tools">
          {(status?.tools || []).map((t) => (
            <div key={t.name} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className={`w-4 h-4 ${t.read_only ? "text-gray-400" : "text-amber-500"}`} />
                  <code className="text-sm font-mono text-gray-900">{t.name}</code>
                </div>
                {t.read_only ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700"><ShieldCheck className="w-3 h-3" /> read-only</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800" title="Kräver scope elido.write"><ShieldAlert className="w-3 h-3" /> write · elido.write</span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">{t.title}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {(t.data_sources || []).map((ds) => (
                  <span key={ds} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] font-mono">{ds}</span>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Senaste lyckade: {t.last_success || "—"}</p>
            </div>
          ))}
        </div>
      )}

      {/* SOURCES */}
      {tab === "sources" && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="tab-content-sources">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Datakälla</th><th className="text-left px-4 py-3">Typ</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Antal</th></tr>
            </thead>
            <tbody>
              {(status?.data_sources || []).map((d, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-900">{d.label}</td>
                  <td className="px-4 py-3"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{d.kind}</span></td>
                  <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                  <td className="px-4 py-3 text-right font-mono text-gray-700">{d.count ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* INSTALL */}
      {tab === "install" && <InstallGuide srv={srv} clients={clients} onCopy={copy} />}

      {/* AUDIT */}
      {tab === "audit" && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="tab-content-audit">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Tid</th><th className="text-left px-4 py-3">Händelse</th><th className="text-left px-4 py-3">Resultat</th><th className="text-left px-4 py-3">Verktyg</th><th className="text-left px-4 py-3">Klient</th></tr>
            </thead>
            <tbody>
              {audit.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Ingen aktivitet</td></tr>}
              {audit.map((e, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{e.ts}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.event}</td>
                  <td className="px-4 py-2"><span className={`text-xs ${e.result === "ok" ? "text-green-600" : e.result === "error" || e.result === "reuse_detected" || e.result === "denied" ? "text-red-600" : "text-gray-500"}`}>{e.result}</span></td>
                  <td className="px-4 py-2 text-xs">{e.tool || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500 truncate max-w-[160px]">{e.oauth_client_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClientCard({ c, onRotate, onToggle, onSaveRedirects, onCopy, onSetWrite }) {
  const [uris, setUris] = useState((c.redirect_uris || []).join("\n"));
  const [editing, setEditing] = useState(false);
  const writeEnabled = !!c.write_enabled || (c.scopes || []).includes("elido.write");
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`client-card-${c.client_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{c.name}</span>
            <StatusPill status={c.active ? "ok" : "error"} />
            {writeEnabled ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800" data-testid={`write-badge-${c.client_id}`}><ShieldAlert className="w-3 h-3" /> write aktiverat</span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700" data-testid={`readonly-badge-${c.client_id}`}><ShieldCheck className="w-3 h-3" /> read-only</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-xs font-mono text-gray-600 truncate">{c.client_id}</code>
            <button onClick={() => onCopy(c.client_id, "client_id kopierat")} className="text-gray-400 hover:text-gray-700"><Copy className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`client-scopes-${c.client_id}`}>
            <span className="text-xs text-gray-400 mr-1">Scopes:</span>
            {(c.scopes || []).length === 0 && <span className="text-xs text-gray-400">—</span>}
            {(c.scopes || []).map((s) => (
              <span key={s} className={`px-1.5 py-0.5 rounded text-[11px] font-mono ${s === "elido.write" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`}>{s}</span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">Skapad av {c.created_by} · {c.created_at} · {c.active_sessions} aktiva sessioner</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <button onClick={() => onSetWrite(c.client_id, !writeEnabled)} data-testid={`write-toggle-${c.client_id}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg ${writeEnabled ? "text-amber-700 border border-amber-300 hover:bg-amber-50" : "text-gray-700 border border-gray-200 hover:bg-gray-50"}`}
            title={writeEnabled ? "Inaktivera skriv-behörighet (elido.write)" : "Aktivera skriv-behörighet (elido.write)"}>
            {writeEnabled ? <><ShieldCheck className="w-3.5 h-3.5" /> Inaktivera write</> : <><ShieldAlert className="w-3.5 h-3.5" /> Aktivera write</>}
          </button>
          <button onClick={() => onRotate(c.client_id)} data-testid={`rotate-${c.client_id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"><KeyRound className="w-3.5 h-3.5" /> Rotera</button>
          <button onClick={() => onToggle(c.client_id, c.active)} data-testid={`toggle-${c.client_id}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg ${c.active ? "text-red-600 border border-red-200 hover:bg-red-50" : "text-green-600 border border-green-200 hover:bg-green-50"}`}>
            {c.active ? <><PowerOff className="w-3.5 h-3.5" /> Inaktivera</> : <><Power className="w-3.5 h-3.5" /> Aktivera</>}
          </button>
        </div>
      </div>
      {writeEnabled && (
        <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`write-reconnect-note-${c.client_id}`}>
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">
            Denna klient kan utföra SEO-skrivningar (apply_seo_changes / rollback_seo_change) via elido.write.
            Ändrade du precis behörigheten? Koppla då om ChatGPT-anslutningen — en pågående session får inte det nya scopet förrän den återansluter.
          </p>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">Redirect-URI:er (exakt matchning)</span>
          <button onClick={() => setEditing(!editing)} className="text-xs" style={{ color: TEAL }}>{editing ? "Avbryt" : "Redigera"}</button>
        </div>
        {editing ? (
          <div>
            <textarea value={uris} onChange={(e) => setUris(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-xs" data-testid={`redirects-edit-${c.client_id}`} />
            <button onClick={() => { onSaveRedirects(c.client_id, uris.split("\n").map((u) => u.trim()).filter(Boolean)); setEditing(false); }}
              className="mt-2 px-3 py-1.5 text-xs text-white rounded-lg" style={{ background: TEAL }} data-testid={`save-redirects-${c.client_id}`}>Spara</button>
          </div>
        ) : (
          <div className="space-y-1">
            {(c.redirect_uris || []).length === 0 && <span className="text-xs text-gray-400">Inga registrerade</span>}
            {(c.redirect_uris || []).map((u, i) => <code key={i} className="block text-xs font-mono text-gray-600 truncate">{u}</code>)}
          </div>
        )}
      </div>
    </div>
  );
}

function InstallGuide({ srv, clients, onCopy }) {
  const active = clients.find((c) => c.active) || clients[0];
  const steps = [
    "Öppna ChatGPT → Inställningar → Connectors → skapa en connector i developer mode.",
    "Ange MCP-endpoint (nedan) som server-URL.",
    "Välj OAuth som autentiseringstyp.",
    "Klistra in client_id nedan. client_secret visas endast när klienten skapas/roteras — skapa en klient under fliken OAuth-klienter om du saknar den.",
    "Slutför anslutningen. Om ChatGPTs callback-URI inte är registrerad nekas första försöket säkert och URI:n dyker upp som kandidat under OAuth-klienter → godkänn den och anslut igen.",
    "Anslut med scope elido.read. Om klienten är write-aktiverad kan du även begära elido.write (kräver explicit consent). offline_access ges automatiskt för refresh-token.",
  ];
  const testQueries = [
    "Ge mig en verksamhetsöversikt för de senaste 7 dagarna.",
    "Visa Kund 360 för elido_official.",
    "Hur presterar elido i Search Console de senaste 28 dagarna jämfört med föregående period?",
    "Analysera SEO för sidan /hemsidor.",
  ];
  return (
    <div className="space-y-4" data-testid="tab-content-install">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Anslut i ChatGPT</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <Field label="MCP-endpoint" value={srv.mcp_endpoint} />
          <Field label="Auth-typ" value="OAuth 2.1 (PKCE S256)" mono={false} canCopy={false} />
          <Field label="Scope" value={srv.scope} />
          <Field label="client_id" value={active?.client_id} />
        </div>
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3 text-sm text-gray-700">
              <span className="shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center" style={{ background: TEAL }}>{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="install-scopes">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Scopes (behörigheter)</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-mono bg-blue-50 text-blue-700 shrink-0 mt-0.5">elido.read</span>
            <p className="text-sm text-gray-700">Läsbehörighet till de 14 read-only verktygen (verksamhet, leads, Google Ads/GA4/Search Console, SEO-analys, innehåll). Ges till varje klient som standard.</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-mono bg-amber-100 text-amber-800 shrink-0 mt-0.5">elido.write</span>
            <p className="text-sm text-gray-700">Skrivbehörighet till de 2 SEO-action-verktygen (<code className="font-mono">apply_seo_changes</code>, <code className="font-mono">rollback_seo_change</code>). Ges ALDRIG automatiskt — en admin måste aktivera den per klient (fliken OAuth-klienter). Kräver dessutom explicit consent i ChatGPT. Efter aktivering måste anslutningen kopplas om för att sessionen ska få scopet.</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-600 shrink-0 mt-0.5">offline_access</span>
            <p className="text-sm text-gray-700">Ger en refresh-token så att ChatGPT kan förnya åtkomsten utan att logga in på nytt. Läggs till automatiskt.</p>
          </div>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Testfrågor efter anslutning</h3>
        <div className="space-y-2">
          {testQueries.map((q, i) => (
            <div key={i} className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-gray-700">{q}</span>
              <button onClick={() => onCopy(q, "Fråga kopierad")} className="text-gray-400 hover:text-gray-700"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Anslutningen används från ChatGPT på webben. 14 verktyg är read-only; endast apply_seo_changes och rollback_seo_change skriver och kräver elido.write.</p>
      </div>
    </div>
  );
}
